import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { formatDateOnly, formatCreatedAt } from "../utils/formatDate";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api from "../utils/axios";
import {
  Send, Download, CalendarDays, Smile, CornerUpLeft,
  MessageSquare, X, Users, Maximize2, Minimize2, Search,
  Bold, Italic, List, Code, GripVertical,
} from "lucide-react";
import { DESIGNATION_LABELS } from "../utils/designations";
import { hasRole } from "../utils/roles";
import { blockNegativeKey, blockWheelChange } from "../utils/numberInput";

// ── Avatar colours ────────────────────────────────────────────────────────────
const AVATAR_COLORS = [
  { bg:"bg-violet-100 dark:bg-violet-900/40", text:"text-violet-700 dark:text-violet-300", ring:"ring-violet-300 dark:ring-violet-600" },
  { bg:"bg-sky-100 dark:bg-sky-900/40",       text:"text-sky-700 dark:text-sky-300",       ring:"ring-sky-300 dark:ring-sky-600"       },
  { bg:"bg-emerald-100 dark:bg-emerald-900/40",text:"text-emerald-700 dark:text-emerald-300",ring:"ring-emerald-300 dark:ring-emerald-600"},
  { bg:"bg-rose-100 dark:bg-rose-900/40",      text:"text-rose-700 dark:text-rose-300",      ring:"ring-rose-300 dark:ring-rose-600"    },
  { bg:"bg-amber-100 dark:bg-amber-900/40",    text:"text-amber-700 dark:text-amber-300",    ring:"ring-amber-300 dark:ring-amber-600"  },
  { bg:"bg-fuchsia-100 dark:bg-fuchsia-900/40",text:"text-fuchsia-700 dark:text-fuchsia-300",ring:"ring-fuchsia-300 dark:ring-fuchsia-600"},
];
function getAvatarColor(name=""){
  let h=0; for(let i=0;i<name.length;i++) h=name.charCodeAt(i)+((h<<5)-h);
  return AVATAR_COLORS[Math.abs(h)%AVATAR_COLORS.length];
}
function getInitials(name=""){
  return name.split(" ").filter(Boolean).map(w=>w[0].toUpperCase()).slice(0,2).join("");
}
function parseActor(details=""){
  const m=details?.match(/by\s+(.+?)(?:\s*\(|$)/i);
  return m?m[1].trim():null;
}

// ── Timeline styles ───────────────────────────────────────────────────────────
const EVENT_STYLES={
  "campaign created":{ dot:"from-emerald-400 to-emerald-600", icon:"✦" },
  "delivery":        { dot:"from-blue-400 to-blue-600",       icon:"⇪" },
  "link added":      { dot:"from-violet-400 to-violet-600",   icon:"⊕" },
  "updated":         { dot:"from-amber-400 to-amber-500",     icon:"✎" },
  default:           { dot:"from-[#708f03]/80 to-[#cffa39]/80",icon:"◈"},
};
function getEventStyle(type=""){
  const k=type.toLowerCase();
  for(const [key,val] of Object.entries(EVENT_STYLES)){
    if(key!=="default"&&k.includes(key)) return val;
  }
  return EVENT_STYLES.default;
}

// ── Message labels ────────────────────────────────────────────────────────────
const MSG_LABELS = {
  task:   { color:"#22c55e", bg:"rgba(34,197,94,0.12)",  border:"rgba(34,197,94,0.35)",  text:"Task"   },
  note:   { color:"#3b82f6", bg:"rgba(59,130,246,0.12)", border:"rgba(59,130,246,0.35)", text:"Note"   },
  alert:  { color:"#ef4444", bg:"rgba(239,68,68,0.12)",  border:"rgba(239,68,68,0.35)",  text:"Alert"  },
  query:  { color:"#a855f7", bg:"rgba(168,85,247,0.12)", border:"rgba(168,85,247,0.35)", text:"Query"  },
  info:   { color:"#06a0c9", bg:"rgba(6,160,201,0.12)", border:"rgba(6,160,201,0.35)", text:"Info"   },
  update: { color:"#f59e0b", bg:"rgba(245,158,11,0.12)", border:"rgba(245,158,11,0.35)", text:"Update" },
};

// ── Emoji data ────────────────────────────────────────────────────────────────
const EMOJI_CATEGORIES = {
  "😊 Reactions": ["👍","❤️","🔥","🎉","✅","😂","😮","😢","🙏","💯","⭐","💪","👏","🚀","💡","🔔","⚠️","❌","✔️","📌"],
  "😀 Faces":     ["😀","😃","😄","😁","😆","🥹","😅","🤣","😊","😇","🙂","😉","😌","😍","🥰","😘","😋","😎","🤩","🥳"],
  "👋 People":    ["👋","🤝","🫡","👌","✌️","🤞","🤟","🤙","💪","🫶","🙌","👐","🤲","🫂","🧑‍💻","👩‍💻","🧑‍🎨","👨‍🎨","🦸","🦹"],
  "🎯 Objects":   ["📋","📝","📌","📍","🗂️","📁","📊","📈","📉","🗓️","⏰","🔑","🔒","🔓","💰","🎯","🏆","🥇","💎","🔮"],
  "⚡ Symbols":   ["⚡","🌟","💫","✨","🎊","🎈","🎁","🎀","🏅","🎖️","🔴","🟠","🟡","🟢","🔵","🟣","⚫","⚪","🟤","🔶"],
};
const QUICK_REACTIONS = ["👍","❤️","🔥","😂","😮","🎉","✅","💯","🙏","👏"];

// ── Role label map ────────────────────────────────────────────────────────────
// Derived from the same shared designation map used across the app, so
// "csm" / "cm" / "manager" always render as their full names in chat badges.
const ROLE_LABELS = Object.fromEntries(
  Object.entries(DESIGNATION_LABELS).map(([k, v]) => [k.toLowerCase(), v])
);

// ── Render markdown-lite message ──────────────────────────────────────────────
function renderMessage(text=""){
  if(!text) return "";
  return text
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g,     "<em>$1</em>")
    .replace(/`(.+?)`/g,       "<code style='background:rgba(0,0,0,0.12);padding:1px 4px;border-radius:3px;font-family:monospace;font-size:11px'>$1</code>")
    .replace(/^• (.+)$/gm,     "<span style='display:block'>• $1</span>")
    .replace(/\n/g,            "<br/>");
}

// ═══════════════════════════════════════════════════════════════════════════════
// EMOJI PICKER
// ═══════════════════════════════════════════════════════════════════════════════
function EmojiPicker({ onSelect, onClose }){
  const [cat, setCat] = useState(Object.keys(EMOJI_CATEGORIES)[0]);
  const ref = useRef(null);
  useEffect(()=>{
    const h=(e)=>{ if(ref.current&&!ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown",h);
    return ()=>document.removeEventListener("mousedown",h);
  },[onClose]);
  return(
    <div ref={ref}
      className="absolute bottom-full mb-2 left-0 w-72 rounded-2xl shadow-2xl z-50
        bg-card border border-border overflow-hidden">
      {/* Category tabs */}
      <div className="flex gap-0.5 p-1.5 bg-muted/30 border-b border-border overflow-x-auto scrollbar-none">
        {Object.keys(EMOJI_CATEGORIES).map(c=>(
          <button key={c} onClick={()=>setCat(c)}
            className={`flex-shrink-0 text-[11px] px-2 py-1 rounded-lg transition-all font-medium
              ${cat===c
                ? "bg-[#06a0c9]/20 text-[#06a0c9] dark:bg-[#cffa39]/20 dark:text-[#cffa39]"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/50"}`}>
            {c.split(" ")[0]}
          </button>
        ))}
      </div>
      {/* Emoji grid */}
      <div className="p-2 grid grid-cols-8 gap-0.5 max-h-44 overflow-y-auto chat-scroll">
        {EMOJI_CATEGORIES[cat].map(e=>(
          <button key={e} onClick={()=>{ onSelect(e); onClose(); }}
            className="w-8 h-8 flex items-center justify-center text-lg rounded-lg
              hover:bg-muted transition-all hover:scale-125 transform">
            {e}
          </button>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CHAT MESSAGE BUBBLE
// ═══════════════════════════════════════════════════════════════════════════════
function ChatMessage({ m, loggedUser, onReply, onReact, reactions={} }){
  const isMine   = m.senderName === loggedUser.name;
  const color    = getAvatarColor(m.senderName||"");
  const initials = getInitials(m.senderName||"");
  const [showEmoji, setShowEmoji]       = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const emojiRef = useRef(null);

  useEffect(()=>{
    if(!showReactions) return;
    const h=(e)=>{ if(emojiRef.current&&!emojiRef.current.contains(e.target)) setShowReactions(false); };
    document.addEventListener("mousedown",h);
    return ()=>document.removeEventListener("mousedown",h);
  },[showReactions]);

  const msgReactions = reactions[m.id]||{};
const roleLabel = m.senderRole
  ? m.senderRole.split(",").map((r) => ROLE_LABELS[r.trim().toLowerCase()] || r.trim()).join(" / ")
  : null;
  const label        = m.label ? MSG_LABELS[m.label] : null;

  // Bubble style — label overrides default neon bubble
  const bubbleStyle = label
    ? { background: label.bg, borderColor: label.border, border: "1px solid" }
    : {};
  const bubbleCls = label
    ? `px-3 py-2 rounded-2xl text-xs leading-relaxed shadow-sm ${isMine?"rounded-br-sm":"rounded-bl-sm"}`
    : `px-3 py-2 rounded-2xl text-xs leading-relaxed shadow-sm
       ${isMine
         ? "rounded-br-sm bg-[#06a0c9]/15 border border-[#06a0c9]/30 text-foreground dark:bg-[#cffa39]/15 dark:border-[#cffa39]/25"
         : "rounded-bl-sm bg-card border border-border text-foreground"}`;

  return(
    <div className={`group flex gap-2 mb-2 ${isMine?"flex-row-reverse":"flex-row"}`}>
      {/* Avatar */}
      <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center
        text-[10px] font-bold ring-2 self-end mb-0.5 ${color.bg} ${color.text} ${color.ring}`}>
        {initials}
      </div>

      <div className={`flex flex-col max-w-[75%] ${isMine?"items-end":"items-start"}`}>
        {/* Name row */}
        <div className={`flex items-center gap-1.5 mb-0.5 flex-wrap ${isMine?"flex-row-reverse":""}`}>
          <span className="text-[11px] font-semibold text-foreground/80 leading-none">{m.senderName}</span>
          {roleLabel&&(
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none
              ${isMine
                ?"bg-[#06a0c9]/15 text-[#06a0c9] dark:bg-[#cffa39]/15 dark:text-[#cffa39]"
                :"bg-muted text-muted-foreground"}`}>
              {roleLabel}
            </span>
          )}
          {label&&(
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none"
              style={{background:label.bg, color:label.color, border:`1px solid ${label.border}`}}>
              {label.text}
            </span>
          )}
          <span className="text-[10px] text-muted-foreground leading-none">{formatCreatedAt(m.createdAt)}</span>
        </div>

        {/* Reply preview */}
        {m.replyTo&&(
          <div className="mb-1 px-2 py-1 rounded-lg border-l-2 text-[10px] text-muted-foreground
            bg-muted/40 border-primary/40 max-w-full truncate">
            ↩ {m.replyTo}
          </div>
        )}

        {/* Bubble + actions */}
        <div className="relative">
          {/* Hover actions */}
          <div className={`absolute top-1/2 -translate-y-1/2 flex items-center gap-1
            opacity-0 group-hover:opacity-100 transition-opacity z-10
            ${isMine?"right-full mr-1.5":"left-full ml-1.5"}`}>
            <div className="relative" ref={emojiRef}>
              <button onClick={()=>setShowReactions(s=>!s)}
                className="w-6 h-6 rounded-full flex items-center justify-center
                  bg-background border border-border shadow-sm text-muted-foreground
                  hover:border-[#06a0c9] hover:text-[#06a0c9]
                  dark:hover:border-[#cffa39] dark:hover:text-[#cffa39] transition-all">
                <Smile size={11}/>
              </button>
              {showReactions&&(
                <div className={`absolute bottom-full mb-1.5 flex gap-1 p-1.5 rounded-xl
                  bg-background border border-border shadow-lg z-30
                  ${isMine?"right-0":"left-0"}`}>
                  {QUICK_REACTIONS.map(e=>(
                    <button key={e} onClick={()=>{ onReact(m.id,e); setShowReactions(false); }}
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-base
                        hover:bg-muted transition-all hover:scale-125 transform">
                      {e}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={()=>onReply(m)}
              className="w-6 h-6 rounded-full flex items-center justify-center
                bg-background border border-border shadow-sm text-muted-foreground
                hover:border-[#06a0c9] hover:text-[#06a0c9]
                dark:hover:border-[#cffa39] dark:hover:text-[#cffa39] transition-all">
              <CornerUpLeft size={11}/>
            </button>
          </div>

          {/* Bubble */}
          <div className={bubbleCls} style={bubbleStyle}
            dangerouslySetInnerHTML={{__html: renderMessage(m.message)}}/>

          {/* Reactions display */}
          {Object.keys(msgReactions).length>0&&(
            <div className={`flex gap-1 mt-1 flex-wrap ${isMine?"justify-end":""}`}>
              {Object.entries(msgReactions).map(([emoji,count])=>(
                <button key={emoji} onClick={()=>onReact(m.id,emoji)}
                  className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px]
                    bg-muted border border-border hover:border-primary/40 transition-colors">
                  {emoji}<span className="text-muted-foreground ml-0.5">{count}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Date separator ────────────────────────────────────────────────────────────
function DateSeparator({ date }){
  return(
    <div className="flex items-center gap-2 my-2">
      <div className="flex-1 h-px bg-border/50"/>
      <span className="text-[9px] font-medium text-muted-foreground px-2 py-0.5
        rounded-full bg-muted/40 border border-border/40 whitespace-nowrap">{date}</span>
      <div className="flex-1 h-px bg-border/50"/>
    </div>
  );
}
function groupByDay(messages){
  const groups=[]; let lastDay="";
  messages.forEach(m=>{
    const day=m.createdAt?new Date(m.createdAt).toDateString():"";
    if(day!==lastDay){ groups.push({type:"date",label:day}); lastDay=day; }
    groups.push({type:"msg",msg:m});
  });
  return groups;
}

// ═══════════════════════════════════════════════════════════════════════════════
// CHAT INPUT BAR — formatting toolbar + emoji + labels
// ═══════════════════════════════════════════════════════════════════════════════
function ChatInputBar({ value, onChange, onSend, disabled, replyTarget, onCancelReply, selectedLabel, onLabelChange }){
  const textRef   = useRef(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  // Auto-resize
  useEffect(()=>{
    const el=textRef.current; if(!el) return;
    el.style.height="auto";
    el.style.height=Math.min(el.scrollHeight,120)+"px";
  },[value]);

  const handleKey=(e)=>{ if(e.key==="Enter"&&!e.shiftKey){ e.preventDefault(); onSend(); } };

  // Wrap selected text with markdown markers
  const wrapSelection=(before,after="")=>{
    const el=textRef.current; if(!el) return;
    const s=el.selectionStart, e=el.selectionEnd;
    const sel=value.slice(s,e)||"text";
    const next=value.slice(0,s)+before+sel+(after||before)+value.slice(e);
    onChange(next);
    setTimeout(()=>{ el.focus(); el.setSelectionRange(s+before.length,s+before.length+sel.length); },0);
  };

  // Insert at cursor
  const insertAtCursor=(str)=>{
    const el=textRef.current; if(!el) return;
    const s=el.selectionStart;
    onChange(value.slice(0,s)+str+value.slice(s));
    setTimeout(()=>{ el.focus(); el.setSelectionRange(s+str.length,s+str.length); },0);
  };

  const labelEntry = selectedLabel ? MSG_LABELS[selectedLabel] : null;

  return(
    <div className="space-y-1.5">
      {/* Reply preview */}
      {replyTarget&&(
        <div className="flex items-center justify-between px-2.5 py-1.5 rounded-lg
          bg-muted/50 border border-border text-[10px]">
          <span className="text-muted-foreground truncate">
            ↩ Replying to <span className="font-semibold text-foreground">{replyTarget.senderName}</span>
            : "{replyTarget.message.slice(0,45)}{replyTarget.message.length>45?"…":""}"
          </span>
          <button onClick={onCancelReply} className="ml-2 text-muted-foreground hover:text-foreground flex-shrink-0"><X size={11}/></button>
        </div>
      )}

      {/* Formatting toolbar */}
      <div className="flex items-center gap-0.5 px-1">
        {[
          { icon:<Bold size={11}/>,   title:"Bold",   action:()=>wrapSelection("**") },
          { icon:<Italic size={11}/>, title:"Italic", action:()=>wrapSelection("*") },
          { icon:<Code size={11}/>,   title:"Code",   action:()=>wrapSelection("`") },
          { icon:<List size={11}/>,   title:"Bullet", action:()=>insertAtCursor("• ") },
        ].map((t,i)=>(
          <button key={i} onClick={t.action} title={t.title}
            className="w-6 h-6 rounded-md flex items-center justify-center
              text-muted-foreground hover:text-foreground hover:bg-muted
              transition-colors text-[10px]">
            {t.icon}
          </button>
        ))}
        <div className="w-px h-4 bg-border mx-1"/>
        {/* Emoji button */}
        <div className="relative">
          <button onClick={()=>setShowEmojiPicker(s=>!s)}
            className="w-6 h-6 rounded-md flex items-center justify-center
              text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
            <Smile size={11}/>
          </button>
          {showEmojiPicker&&(
            <EmojiPicker
              onSelect={insertAtCursor}
              onClose={()=>setShowEmojiPicker(false)}
            />
          )}
        </div>
      </div>

      {/* Textarea + send */}
      <div className="flex items-end gap-2">
        <textarea
          ref={textRef}
          disabled={disabled}
          placeholder="Type a message"
          value={value}
          onChange={e=>onChange(e.target.value)}
          onKeyDown={handleKey}
          rows={1}
          className="flex-1 resize-none rounded-xl border border-border bg-background/80
            px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground
            focus:outline-none focus:ring-2 focus:ring-[#06a0c9]/40 dark:focus:ring-[#cffa39]/40
            focus:border-[#06a0c9] dark:focus:border-[#cffa39]
            transition-all overflow-hidden leading-relaxed"
          style={{
            minHeight:"36px", maxHeight:"120px",
            ...(labelEntry ? { borderColor: labelEntry.border, boxShadow:`0 0 0 2px ${labelEntry.border}` } : {})
          }}
        />
        <button
          disabled={disabled||!value.trim()}
          onClick={onSend}
          className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center
            transition-all shadow-sm disabled:opacity-40 disabled:cursor-not-allowed
            bg-[#06a0c9]/20 border border-[#06a0c9]/50 text-[#06a0c9]
            hover:bg-[#06a0c9]/30 hover:border-[#06a0c9]
            dark:bg-[#cffa39]/20 dark:border-[#cffa39]/50 dark:text-[#cffa39]
            dark:hover:bg-[#cffa39]/30 dark:hover:border-[#cffa39]">
          {disabled?<span className="text-xs font-bold">…</span>:<Send size={13}/>}
        </button>
      </div>

      {/* Label selector */}
      <div className="flex items-center gap-1.5 flex-wrap px-0.5">
        <span className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wide">Label:</span>
        {Object.entries(MSG_LABELS).map(([key,lbl])=>(
          <button key={key}
            onClick={()=>onLabelChange(selectedLabel===key?null:key)}
            className="text-[9px] font-bold px-2 py-0.5 rounded-full transition-all border"
            style={{
              color: lbl.color,
              background: selectedLabel===key ? lbl.bg : "transparent",
              borderColor: selectedLabel===key ? lbl.color : "rgba(128,128,128,0.25)",
              transform: selectedLabel===key ? "scale(1.05)" : "scale(1)",
            }}>
            {lbl.text}
          </button>
        ))}
        {selectedLabel&&(
          <button onClick={()=>onLabelChange(null)}
            className="text-[9px] text-muted-foreground hover:text-foreground transition-colors px-1">
            ✕ clear
          </button>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// FLOATING CHAT PANEL — resizable, maximizable, neon border
// ═══════════════════════════════════════════════════════════════════════════════
function FloatingChatPanel({
  open, onClose, messages, loggedUser,
  message, onMessageChange, onSend, sending,
  replyTarget, onReply, onCancelReply,
  reactions, onReact, campaignName,
  selectedLabel, onLabelChange,
}){
  const endRef      = useRef(null);
  const panelRef    = useRef(null);
  const [maximized,  setMaximized]  = useState(false);
  const [panelSize,  setPanelSize]  = useState({ w:400, h:540 });
  const [searchQuery,setSearchQuery]= useState("");
  const [showSearch, setShowSearch] = useState(false);
  const resizing    = useRef(null);

  // Scroll to bottom on new msgs
  useEffect(()=>{ if(open) endRef.current?.scrollIntoView({behavior:"smooth"}); },[messages,open]);

  // Resize drag logic
  const onResizeStart=(e)=>{
    e.preventDefault();
    resizing.current={ startX:e.clientX, startY:e.clientY, startW:panelSize.w, startH:panelSize.h };
    const onMove=(ev)=>{
      if(!resizing.current) return;
      const dx=resizing.current.startX-ev.clientX; // dragging left = wider
      const dy=resizing.current.startY-ev.clientY; // dragging up = taller
      setPanelSize({
        w: Math.min(780, Math.max(320, resizing.current.startW+dx)),
        h: Math.min(window.innerHeight-80, Math.max(400, resizing.current.startH+dy)),
      });
    };
    const onUp=()=>{ resizing.current=null; window.removeEventListener("mousemove",onMove); window.removeEventListener("mouseup",onUp); };
    window.addEventListener("mousemove",onMove);
    window.addEventListener("mouseup",onUp);
  };

  const filtered = searchQuery.trim()
    ? messages.filter(m=>m.message?.toLowerCase().includes(searchQuery.toLowerCase())||
        m.senderName?.toLowerCase().includes(searchQuery.toLowerCase()))
    : messages;

  const panelStyle = maximized
    ? { width:"calc(100vw - 2rem)", height:"calc(100vh - 5rem)", bottom:"1rem", right:"1rem" }
    : { width:`${panelSize.w}px`, height:`${panelSize.h}px`, bottom:"1rem", right:"1rem" };

  return(
    <>
      {open&&<div className="fixed inset-0 z-40 lg:hidden bg-black/20" onClick={onClose}/>}

      <div
        ref={panelRef}
        className={`fixed z-50 flex flex-col rounded-2xl shadow-2xl overflow-hidden
          neon-card
          transition-all duration-300 ease-in-out
          ${open?"opacity-100 translate-y-0 pointer-events-auto":"opacity-0 translate-y-8 pointer-events-none"}`}
        style={panelStyle}>

        {/* Resize handle — top-left corner */}
        {!maximized&&(
          <div
            onMouseDown={onResizeStart}
            className="absolute top-0 left-0 w-5 h-5 z-20 cursor-nw-resize
              flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
            title="Drag to resize">
            <GripVertical size={10} className="text-muted-foreground rotate-45"/>
          </div>
        )}

        {/* ── Header ── */}
        <div className="flex-shrink-0 flex items-center justify-between px-4 py-2.5
          border-b border-border/60 bg-muted/20">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-2 h-2 rounded-full flex-shrink-0
              bg-[#06a0c9] dark:bg-[#cffa39] animate-pulse
              shadow-[0_0_6px_rgba(6,160,201,0.9)] dark:shadow-[0_0_6px_rgba(207,250,57,0.9)]"/>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-foreground leading-none truncate">Campaign Chat</p>
              <p className="text-[9px] text-muted-foreground mt-0.5 truncate">{campaignName||"All Members"}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full
              bg-[#06a0c9]/15 text-[#06a0c9] border border-[#06a0c9]/30
              dark:bg-[#cffa39]/15 dark:text-[#cffa39] dark:border-[#cffa39]/30">
              {messages.length}
            </span>
            <button onClick={()=>setShowSearch(s=>!s)}
              className={`w-6 h-6 rounded-lg flex items-center justify-center transition-colors
                ${showSearch?"text-[#06a0c9] dark:text-[#cffa39]":"text-muted-foreground hover:text-foreground"}
                hover:bg-muted`}>
              <Search size={12}/>
            </button>
            <button onClick={()=>setMaximized(s=>!s)}
              className="w-6 h-6 rounded-lg flex items-center justify-center
                text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              title={maximized?"Restore":"Maximize"}>
              {maximized?<Minimize2 size={12}/>:<Maximize2 size={12}/>}
            </button>
            <button onClick={onClose}
              className="w-6 h-6 rounded-lg flex items-center justify-center
                text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
              <X size={12}/>
            </button>
          </div>
        </div>

        {/* ── Search bar ── */}
        {showSearch&&(
          <div className="flex-shrink-0 px-3 py-1.5 border-b border-border/40 bg-muted/10">
            <div className="relative">
              <Search size={11} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"/>
              <input
                value={searchQuery}
                onChange={e=>setSearchQuery(e.target.value)}
                placeholder="Search messages…"
                className="w-full h-7 pl-7 pr-3 text-[11px] rounded-lg border border-border bg-background
                  focus:outline-none focus:border-[#06a0c9] dark:focus:border-[#cffa39]
                  text-foreground placeholder:text-muted-foreground"
              />
              {searchQuery&&(
                <button onClick={()=>setSearchQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X size={10}/>
                </button>
              )}
            </div>
            {searchQuery&&(
              <p className="text-[9px] text-muted-foreground mt-1 px-0.5">
                {filtered.length} result{filtered.length!==1?"s":""}
              </p>
            )}
          </div>
        )}

        {/* ── Messages ── */}
        <div className="flex-1 overflow-y-auto px-3 py-2 chat-scroll">
          {groupByDay(filtered).map((item,i)=>
            item.type==="date"
              ? <DateSeparator key={i} date={item.label}/>
              : <ChatMessage
                  key={item.msg.id}
                  m={item.msg}
                  loggedUser={loggedUser}
                  onReply={onReply}
                  onReact={onReact}
                  reactions={reactions}
                />
          )}
          <div ref={endRef}/>
          {messages.length===0&&(
            <div className="h-full flex flex-col items-center justify-center gap-2
              text-muted-foreground py-10">
              <div className="w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center">
                <Users size={16} className="opacity-40"/>
              </div>
              <p className="text-xs">No messages yet. Be the first!</p>
            </div>
          )}
          {searchQuery&&filtered.length===0&&(
            <div className="flex items-center justify-center py-8 text-muted-foreground text-xs">
              No messages match "{searchQuery}"
            </div>
          )}
        </div>

        {/* ── Input ── */}
        <div className="flex-shrink-0 px-3 pb-3 pt-2 border-t border-border/50 bg-muted/5">
          <ChatInputBar
            value={message}
            onChange={onMessageChange}
            onSend={onSend}
            disabled={sending}
            replyTarget={replyTarget}
            onCancelReply={onCancelReply}
            selectedLabel={selectedLabel}
            onLabelChange={onLabelChange}
          />
        </div>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function CampaignDetail(){
  const { id } = useParams();

  const [campaign,      setCampaign]      = useState(null);
  const [timeline,      setTimeline]      = useState([]);
  const [links,         setLinks]         = useState([]);
  const [linkUrl,       setLinkUrl]       = useState("");
  const [materialType,  setMaterialType]  = useState("");
  const [customMaterialName, setCustomMaterialName] = useState("");

  // Delivery — now link-based (Campaign Manager adds, everyone else views)
  const [deliveryLinks,      setDeliveryLinks]      = useState([]);
  const [deliveryUrl,        setDeliveryUrl]        = useState("");
  const [deliveryLabel,      setDeliveryLabel]      = useState("");
  const [deliveryLeadCount,  setDeliveryLeadCount]  = useState("");
  const [savingDeliveryLink, setSavingDeliveryLink]  = useState(false);
  const [editingDeliveryLinkId, setEditingDeliveryLinkId] = useState(null);
  const [editingDeliveryUrl,    setEditingDeliveryUrl]    = useState("");
  const [editingDeliveryLeadCount, setEditingDeliveryLeadCount] = useState("");
  // Flexible date-range filter for the lead-count table below the links
  const [deliveryRangeStart, setDeliveryRangeStart] = useState(null);
  const [deliveryRangeEnd,   setDeliveryRangeEnd]   = useState(null);
  const [deliveryRangeStartOpen, setDeliveryRangeStartOpen] = useState(false);
  const [deliveryRangeEndOpen,   setDeliveryRangeEndOpen]   = useState(false);

  // Unified chat
  const [allMessages,    setAllMessages]    = useState([]);
  const [chatMessage,    setChatMessage]    = useState("");
  const [sendingChat,    setSendingChat]    = useState(false);
  const [chatOpen,       setChatOpen]       = useState(false);
  const [chatReply,      setChatReply]      = useState(null);
  const [chatLabel,      setChatLabel]      = useState(null);
  const [reactions,      setReactions]      = useState({});
  const [unreadCount,    setUnreadCount]    = useState(0);
  const initialLoadDone = useRef(false);
  const lastSeenCount   = useRef(0);

  const [editingLinkId, setEditingLinkId] = useState(null);
  const [editingUrl,    setEditingUrl]    = useState("");
  const [savingLink,    setSavingLink]    = useState(false);

  const editInputRef         = useRef(null);
  const editDeliveryInputRef = useRef(null);

  useEffect(()=>{
    if(!editingLinkId) return;
    const h=(e)=>{ if(editInputRef.current&&!editInputRef.current.contains(e.target)){setEditingLinkId(null);setEditingUrl("");} };
    document.addEventListener("mousedown",h);
    return ()=>document.removeEventListener("mousedown",h);
  },[editingLinkId]);

  useEffect(()=>{
    if(!editingDeliveryLinkId) return;
    const h=(e)=>{ if(editDeliveryInputRef.current&&!editDeliveryInputRef.current.contains(e.target)){setEditingDeliveryLinkId(null);setEditingDeliveryUrl("");setEditingDeliveryLeadCount("");} };
    document.addEventListener("mousedown",h);
    return ()=>document.removeEventListener("mousedown",h);
  },[editingDeliveryLinkId]);

  // ── Data loader ───────────────────────────────────────────────────────────
  const load=useCallback(async(isInitial=false)=>{
    try{
      const [
        {data:c},{data:msgs},
        {data:t},{data:l},{data:d},
      ]=await Promise.all([
        api.get(`/campaigns/${id}`),
        api.get(`/campaigns/${id}/messages`),
        api.get(`/campaigns/${id}/timeline`),
        api.get(`/campaigns/${id}/links`),
        api.get(`/campaigns/${id}/delivery-links`),
      ]);
      setCampaign(c);
      setTimeline(t.items||[]);
      setLinks(l.items||[]);
      setDeliveryLinks(d.items||[]);

      const newMsgs = msgs.items||[];
      setAllMessages(newMsgs);

      if(isInitial){
        // First load: set baseline, show NO badge
        lastSeenCount.current = newMsgs.length;
        setUnreadCount(0);
        initialLoadDone.current = true;
      } else if(!chatOpen){
        // Poll: only count genuinely NEW messages since last seen
        const newCount = newMsgs.length - lastSeenCount.current;
        if(newCount>0) setUnreadCount(prev=>prev+newCount);
        lastSeenCount.current = newMsgs.length;
      }
    }catch(err){ console.error("Load error:",err); }
  },[id, chatOpen]);

  useEffect(()=>{ load(true); },[id]);

  // Mark read when panel opens
  useEffect(()=>{
    if(chatOpen){
      lastSeenCount.current = allMessages.length;
      setUnreadCount(0);
    }
  },[chatOpen]);

  // Poll every 15s
  useEffect(()=>{
    const t=setInterval(()=>load(false),15000);
    return ()=>clearInterval(t);
  },[load]);

  const loggedUser = JSON.parse(localStorage.getItem("user"))||{};
  const role=(loggedUser.role||loggedUser.designation||"").toLowerCase();
const isAdmin = hasRole(loggedUser, "admin", "ceo");
const isCM    = hasRole(loggedUser, "cm");
const isCSM   = hasRole(loggedUser, "csm");

  // Admin/CEO are kept as a super-user override on every permission below.
  // Drop the `|| isAdmin` on any line if you'd rather lock Admin out too.
  const permissions={
    canEditOverview:     isAdmin||isCM||isCSM, // CM & CSM both reach the Edit page; field-level rules live in CampaignEdit.js
    canAddMaterial:      isCSM||isAdmin,
    canEditMaterialLink: isCSM||isAdmin,
    canAddDeliveryLink:  isCM||isAdmin,
    canEditDeliveryLink: isCM||isAdmin,
  };

  useEffect(()=>{
    if(!loggedUser?.role&&!loggedUser?.designation){
      toast.error("Session expired. Please login again.");
      window.location.href="/login";
    }
  },[]);

  // ── Delivery link handlers ───────────────────────────────────────────────
  const saveEditedDeliveryLink=async(f)=>{
    if(!isValidHttpUrl(editingDeliveryUrl)) return toast.error("Enter a valid URL");
    if(editingDeliveryLeadCount!==""&&Number(editingDeliveryLeadCount)<0) return toast.error("Number of leads cannot be negative");
    try{
      await api.put(`/campaigns/${id}/delivery-links/${f.id}`,{
        url:editingDeliveryUrl,
        label:f.label,
        leadCount: editingDeliveryLeadCount===""?null:Number(editingDeliveryLeadCount),
      });
      toast.success("Delivery link updated");
      setEditingDeliveryLinkId(null); setEditingDeliveryUrl(""); setEditingDeliveryLeadCount("");
      load(false);
    }catch(err){ toast.error(err?.response?.data?.error||"Failed to update delivery link"); }
  };

  // Total leads across delivery entries that fall inside the selected date range.
  // No range selected => total across everything.
  const deliveryLeadsInRange = (() => {
    let items = deliveryLinks;
    if (deliveryRangeStart) {
      const start = new Date(deliveryRangeStart); start.setHours(0,0,0,0);
      items = items.filter(f => f.created_at && new Date(f.created_at) >= start);
    }
    if (deliveryRangeEnd) {
      const end = new Date(deliveryRangeEnd); end.setHours(23,59,59,999);
      items = items.filter(f => f.created_at && new Date(f.created_at) <= end);
    }
    return {
      items,
      total: items.reduce((sum, f) => sum + (Number(f.lead_count) || 0), 0),
    };
  })();

  function formatOverviewValue(key,value){
    if(value===null||value===undefined||value==="") return "-";
    if(typeof value==="string"){
      const t=value.trim();
      if((t.startsWith("[")||t.startsWith("{"))&&(t.endsWith("]")||t.endsWith("}"))){
        try{
          const p=JSON.parse(t);
          if(Array.isArray(p)) return p.join(", ");
          if(typeof p==="object") return Object.entries(p).map(([k,v])=>`${k}: ${v}`).join(", ");
        }catch{}
      }
    }
    if(Array.isArray(value)) return value.join(", ");
    if(key.toLowerCase()==="createdat") return formatCreatedAt(value);
    if(key.toLowerCase().includes("date")) return formatDateOnly(value);
    if(typeof value==="object") return Object.entries(value).map(([k,v])=>`${k}: ${v}`).join(", ");
    return String(value);
  }

  const handleReact=(msgId,emoji)=>{
    setReactions(prev=>{ const r={...(prev[msgId]||{})}; r[emoji]=(r[emoji]||0)+1; return {...prev,[msgId]:r}; });
  };

  const sendChatMessage=async()=>{
    if(sendingChat) return;
    if(!chatMessage.trim()) return toast.error("Type a message");
    try{
      setSendingChat(true);
      await api.post(`/campaigns/${id}/messages`,{
        chatType:"all",
        senderRole:  loggedUser.designation,
        senderName:  loggedUser.name,
        senderEmail: loggedUser.email,
        message:     chatMessage,
        label:       chatLabel||null,
        replyTo:     chatReply?`${chatReply.senderName}: ${chatReply.message.slice(0,60)}`:undefined,
      });
      setChatMessage(""); setChatReply(null); setChatLabel(null);
      await load(false);
    }catch{ toast.error("Failed to send message"); }
    finally{ setSendingChat(false); }
  };

  const formatLabel=(key)=>{
    if(DESIGNATION_LABELS[key]) return DESIGNATION_LABELS[key];
    return key.replace(/_/g," ").replace(/([a-z])([A-Z])/g,"$1 $2").replace(/\b\w/g,c=>c.toUpperCase());
  };
  const isValidHttpUrl=(v)=>{try{const u=new URL(v);return u.protocol==="http:"||u.protocol==="https:";}catch{return false;}};
  const logout=()=>{localStorage.removeItem("user");window.location.href="/login";};
  const normalize=(v="")=>v.trim().toLowerCase();
  const materialExists=(name)=>links.some(l=>normalize(l.category)===normalize(name));

  const saveEditedLink=async(f)=>{
    if(!isValidHttpUrl(editingUrl)) return toast.error("Enter a valid URL");
    try{
      await api.put(`/campaigns/${id}/links/${f.id}`,{url:editingUrl,senderName:loggedUser?.name,category:f.category});
      toast.success("Link updated"); setEditingLinkId(null); setEditingUrl(""); load(false);
    }catch{ toast.error("Failed to update link"); }
  };

  const overviewEntries=campaign?Object.entries(campaign).filter(([k])=>!["_id"].includes(k)):[];

  return(
    <div className="mx-auto px-6 py-8">
      <Navbar logout={logout}/>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold truncate mr-4">{campaign?.campaignName||"Campaign"}</h1>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={()=>setChatOpen(s=>!s)}
            className={`relative flex items-center gap-2 px-3.5 py-2 rounded-xl
              text-xs font-semibold transition-all border shadow-sm
              ${chatOpen
                ?"bg-[#06a0c9]/20 border-[#06a0c9]/60 text-[#06a0c9] dark:bg-[#cffa39]/20 dark:border-[#cffa39]/60 dark:text-[#cffa39]"
                :"bg-card border-border text-muted-foreground hover:border-[#06a0c9]/60 hover:text-[#06a0c9] dark:hover:border-[#cffa39]/60 dark:hover:text-[#cffa39]"}`}>
            <MessageSquare size={14}/>
            <span>Chat</span>
            {/* Only show badge when there are genuinely new unseen messages */}
            {unreadCount>0&&!chatOpen&&(
              <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full
                flex items-center justify-center text-[9px] font-bold
                bg-[#06a0c9] text-black dark:bg-[#cffa39] dark:text-black shadow-sm">
                {unreadCount>99?"99+":unreadCount}
              </span>
            )}
          </button>
          <Button asChild variant="outline" data-testid="back-home"><Link to="/">← Back</Link></Button>
        </div>
      </div>

      {/* Overview — full width */}
      <Card className="neon-left neon-card mb-6">
        <CardHeader className="flex-row items-center justify-between pb-3">
          <CardTitle className="text-base">Overview</CardTitle>
          {permissions.canEditOverview&&(
            <Button size="sm" variant="outline" asChild><Link to={`/campaign/${id}/edit`}>Edit</Link></Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-x-6 gap-y-3">
            {overviewEntries.map(([k,v])=>(
              <div key={k} className="min-w-0">
                <p className="text-[12px] font-semibold uppercase tracking-wide overview-label mb-0.5">{formatLabel(k)}</p>
                <p className="text-[12px] text-foreground truncate" title={String(formatOverviewValue(k,v))}>{formatOverviewValue(k,v)}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Second row */}
      <div className="grid gap-6 lg:grid-cols-4">

        {/* Campaign Material — only Client Success Manager can add */}
        <Card className="lg:col-span-1 neon-left neon-card">
          <CardHeader><CardTitle>Campaign Material</CardTitle></CardHeader>
          <CardContent className="max-h-[550px] pt-1 overflow-y-auto">
            {permissions.canAddMaterial&&(
              <div className="flex flex-col gap-3 mb-4">
                <Select value={materialType} onValueChange={selected=>{
                  if(!selected){setMaterialType("");return;}
                  if(selected!=="Other"&&materialExists(selected)){toast.error("Material already added");return;}
                  setMaterialType(selected);
                }}>
                  <SelectTrigger className="text-sm"><SelectValue placeholder="Select Material Type"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="POCs" disabled={materialExists("POCs")}>POCs</SelectItem>
                    <SelectItem value="TAL" disabled={materialExists("TAL")}>TAL</SelectItem>
                    <SelectItem value="Suppression List" disabled={materialExists("Suppression List")}>Suppression List</SelectItem>
                    <SelectItem value="Campaign Specs" disabled={materialExists("Campaign Specs")}>Campaign Specs</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {materialType==="Other"&&(
                  <Input placeholder="Enter material name" value={customMaterialName} onChange={e=>setCustomMaterialName(e.target.value)}/>
                )}
                <div className="flex gap-2">
                  <Input placeholder="Enter SharePoint or project link" value={linkUrl}
                    disabled={!materialType||savingLink} onChange={e=>setLinkUrl(e.target.value)} data-testid="link-input"/>
                  <Button disabled={savingLink||!materialType||!isValidHttpUrl(linkUrl)}
                    onClick={async()=>{
                      if(savingLink) return;
                      if(!materialType) return toast.error("Select material type");
                      if(!linkUrl.trim()) return toast.error("Link is required");
                      if(!isValidHttpUrl(linkUrl)) return toast.error("Enter a valid URL");
                      let finalName=materialType;
                      if(materialType==="Other"){ if(!customMaterialName.trim()) return toast.error("Enter material name"); finalName=customMaterialName; }
                      if(materialExists(finalName)) return toast.error("Material already added");
                      try{
                        setSavingLink(true);
                        await api.post(`/campaigns/${id}/links`,{title:finalName,url:linkUrl,category:finalName,timelineType:`${finalName} Link Added`,senderName:loggedUser?.name,senderEmail:loggedUser?.email});
                        toast.success("Link saved"); setLinkUrl(""); setMaterialType(""); setCustomMaterialName(""); await load(false);
                      }catch(err){ toast.error(err?.response?.data?.error||"Failed to save link"); }
                      finally{ setSavingLink(false); }
                    }}>{savingLink?"Saving…":"Save"}</Button>
                </div>
              </div>
            )}
            <ul className="space-y-2.5">
              {links.map(f=>(
                <li key={f.id} className="group flex items-center justify-between p-3 rounded-xl border transition-all
                  bg-slate-50 hover:bg-slate-100 border-slate-200 hover:border-slate-300
                  dark:bg-white/5 dark:hover:bg-white/10 dark:border-white/10 dark:hover:border-primary/20
                  hover:shadow-sm dark:hover:shadow-md dark:hover:shadow-primary/5">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
                      bg-slate-200 text-slate-600 dark:bg-primary/10 dark:text-primary">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      {editingLinkId===f.id?(
                        <div ref={editInputRef} className="flex items-center gap-1.5">
                          <Input value={editingUrl} onChange={e=>setEditingUrl(e.target.value)}
                            onKeyDown={e=>{ if(e.key==="Enter") saveEditedLink(f); if(e.key==="Escape"){setEditingLinkId(null);setEditingUrl("");} }}
                            className="h-7 text-xs flex-1" autoFocus/>
                          <Button size="sm" variant="outline" className="h-7 text-[11px] px-2 flex-shrink-0" onClick={()=>saveEditedLink(f)}>Save</Button>
                        </div>
                      ):(
                        <a href={f.url} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-xs font-semibold truncate transition-colors text-slate-700 hover:text-blue-600 dark:text-primary dark:hover:text-[#0ff710]">
                          <span className="truncate">{f.category}</span>
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                            <path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                          </svg>
                        </a>
                      )}
                      <div className="flex items-center gap-1 text-[11px] text-muted-foreground mt-1">
                        <CalendarDays size={11}/><span>{formatDateOnly(f.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                  {permissions.canEditMaterialLink&&editingLinkId!==f.id&&(
                    <button onClick={()=>{setEditingLinkId(f.id);setEditingUrl(f.url);}}
                      className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-blue-600 dark:text-primary dark:hover:text-[#0ff710]">
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"/>
                      </svg>
                    </button>
                  )}
                </li>
              ))}
              {links.length===0&&<li className="text-center py-6 text-sm text-muted-foreground">No materials added yet</li>}
            </ul>
          </CardContent>
        </Card>

        {/* Delivery — link-based, only Campaign Manager can add */}
        <Card className="lg:col-span-2 neon-left neon-card">
          <CardHeader><CardTitle>Delivery</CardTitle></CardHeader>
          <CardContent className="max-h-[550px] pt-1 overflow-y-auto">
            {permissions.canAddDeliveryLink&&(
              <div className="flex flex-col gap-3 mb-4">
                <Input
                  placeholder="Label (e.g. Batch 1, Week 3 Drop)"
                  value={deliveryLabel}
                  disabled={savingDeliveryLink}
                  onChange={(e)=>setDeliveryLabel(e.target.value)}
                />
                <Input
                  placeholder="Enter delivery link (SharePoint, Drive, Excel sheet, etc.)"
                  value={deliveryUrl}
                  disabled={savingDeliveryLink}
                  onChange={(e)=>setDeliveryUrl(e.target.value)}
                  data-testid="delivery-link-input"
                />
                <div className="flex gap-2">
                  <Input
                    type="number"
                    min={0}
                    onKeyDown={blockNegativeKey}
                    onWheel={blockWheelChange}
                    placeholder="Number of leads"
                    value={deliveryLeadCount}
                    disabled={savingDeliveryLink}
                    onChange={(e)=>setDeliveryLeadCount(e.target.value)}
                    data-testid="delivery-lead-count-input"
                  />
                  <Button
                    disabled={savingDeliveryLink||!isValidHttpUrl(deliveryUrl)}
                    onClick={async()=>{
                      if(savingDeliveryLink) return;
                      if(!deliveryUrl.trim()) return toast.error("Link is required");
                      if(!isValidHttpUrl(deliveryUrl)) return toast.error("Enter a valid URL");
                      if(deliveryLeadCount!==""&&Number(deliveryLeadCount)<0) return toast.error("Number of leads cannot be negative");
                      try{
                        setSavingDeliveryLink(true);
                        await api.post(`/campaigns/${id}/delivery-links`,{
                          label: deliveryLabel||null, url: deliveryUrl, senderName: loggedUser?.name,
                          leadCount: deliveryLeadCount===""?null:Number(deliveryLeadCount),
                        });
                        toast.success("Delivery link saved");
                        setDeliveryUrl(""); setDeliveryLabel(""); setDeliveryLeadCount("");
                        await load(false);
                      }catch(err){
                        toast.error(err?.response?.data?.error||"Failed to save delivery link");
                      }finally{ setSavingDeliveryLink(false); }
                    }}
                  >
                    {savingDeliveryLink?"Saving…":"Save"}
                  </Button>
                </div>
              </div>
            )}

            <ul className="space-y-2.5 mb-5">
              {deliveryLinks.map(f=>(
                <li key={f.id} className="group flex items-center justify-between p-3 rounded-xl border transition-all
                  bg-slate-50 hover:bg-slate-100 border-slate-200 hover:border-slate-300
                  dark:bg-white/5 dark:hover:bg-white/10 dark:border-white/10 dark:hover:border-primary/20
                  hover:shadow-sm dark:hover:shadow-md dark:hover:shadow-primary/5">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
                      bg-slate-200 text-slate-600 dark:bg-primary/10 dark:text-primary">
                      <Download size={14}/>
                    </div>
                    <div className="flex-1 min-w-0">
                      {editingDeliveryLinkId===f.id?(
                        <div ref={editDeliveryInputRef} className="flex flex-col gap-1.5">
                          <Input value={editingDeliveryUrl} onChange={e=>setEditingDeliveryUrl(e.target.value)}
                            onKeyDown={e=>{ if(e.key==="Escape"){setEditingDeliveryLinkId(null);setEditingDeliveryUrl("");setEditingDeliveryLeadCount("");} }}
                            className="h-7 text-xs flex-1" autoFocus placeholder="Delivery link"/>
                          <div className="flex items-center gap-1.5">
                            <Input type="number" min={0} onKeyDown={blockNegativeKey} onWheel={blockWheelChange}
                              value={editingDeliveryLeadCount} onChange={e=>setEditingDeliveryLeadCount(e.target.value)}
                              placeholder="Number of leads" className="h-7 text-xs flex-1"/>
                            <Button size="sm" variant="outline" className="h-7 text-[11px] px-2 flex-shrink-0" onClick={()=>saveEditedDeliveryLink(f)}>Save</Button>
                          </div>
                        </div>
                      ):(
                        <a href={f.url} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-xs font-semibold truncate transition-colors text-slate-700 hover:text-blue-600 dark:text-primary dark:hover:text-[#0ff710]">
                          <span className="truncate">{f.label||"Delivery Link"}</span>
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                            <path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                          </svg>
                        </a>
                      )}
                      <div className="flex items-center gap-2 text-[11px] text-muted-foreground mt-1 flex-wrap">
                        <span className="flex items-center gap-1"><CalendarDays size={11}/>{formatDateOnly(f.created_at)}</span>
                        <span className="font-semibold text-foreground">
                          {f.lead_count!==null&&f.lead_count!==undefined ? `${Number(f.lead_count).toLocaleString()} leads` : "No lead count yet"}
                        </span>
                        {f.updated_at && (
                          <span className="italic">
                            · updated {formatCreatedAt(f.updated_at)}{f.updated_by?` by ${f.updated_by}`:""}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {permissions.canEditDeliveryLink&&editingDeliveryLinkId!==f.id&&(
                    <button onClick={()=>{setEditingDeliveryLinkId(f.id);setEditingDeliveryUrl(f.url);setEditingDeliveryLeadCount(f.lead_count===null||f.lead_count===undefined?"":String(f.lead_count));}}
                      className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-blue-600 dark:text-primary dark:hover:text-[#0ff710]">
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"/>
                      </svg>
                    </button>
                  )}
                </li>
              ))}
              {deliveryLinks.length===0&&<li className="text-center py-6 text-sm text-muted-foreground">No delivery links added yet</li>}
            </ul>

            {/* Flexible date-range lead-count summary */}
            {deliveryLinks.length>0&&(
              <div className="border-t border-border pt-4">
                <p className="text-xs font-semibold text-muted-foreground mb-2">Lead count by date range</p>
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <input type="date" data-testid="delivery-range-start"
                    className="neon-input h-8 px-2 rounded-lg text-xs bg-transparent border border-border"
                    value={deliveryRangeStart||""} onChange={e=>setDeliveryRangeStart(e.target.value||null)} />
                  <span className="text-xs text-muted-foreground">to</span>
                  <input type="date" data-testid="delivery-range-end"
                    className="neon-input h-8 px-2 rounded-lg text-xs bg-transparent border border-border"
                    value={deliveryRangeEnd||""} onChange={e=>setDeliveryRangeEnd(e.target.value||null)} />
                  {(deliveryRangeStart||deliveryRangeEnd)&&(
                    <button className="text-[11px] text-primary hover:underline"
                      onClick={()=>{setDeliveryRangeStart(null);setDeliveryRangeEnd(null);}}>
                      Clear
                    </button>
                  )}
                  <span className="ml-auto text-sm font-semibold">
                    Total: <span className="text-primary">{deliveryLeadsInRange.total.toLocaleString()}</span> leads
                  </span>
                </div>

                <div className="max-h-40 overflow-y-auto rounded-lg border border-border">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40 sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1.5 font-medium">Batch</th>
                        <th className="text-left px-2 py-1.5 font-medium">Date</th>
                        <th className="text-right px-2 py-1.5 font-medium">Leads</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deliveryLeadsInRange.items.map(f=>(
                        <tr key={f.id} className="border-t border-border/60">
                          <td className="px-2 py-1.5 truncate max-w-[120px]">{f.label||"Delivery Link"}</td>
                          <td className="px-2 py-1.5">{formatDateOnly(f.created_at)}</td>
                          <td className="px-2 py-1.5 text-right">{f.lead_count!==null&&f.lead_count!==undefined?Number(f.lead_count).toLocaleString():"—"}</td>
                        </tr>
                      ))}
                      {deliveryLeadsInRange.items.length===0&&(
                        <tr><td colSpan={3} className="text-center py-4 text-muted-foreground">No entries in this range</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Timeline */}
        <Card className="lg:col-span-1 neon-left neon-card">
          <CardHeader><CardTitle>Timeline</CardTitle></CardHeader>
          <CardContent className="max-h-[590px] overflow-y-auto pr-1">
            <ul className="relative" data-testid="timeline">
              <div className="absolute left-[13px] top-4 bottom-4 w-px bg-gradient-to-b from-primary/40 via-border/60 to-transparent pointer-events-none"/>
              {timeline.map((ev,i)=>{
                const actor  = parseActor(ev.details||"");
                const style  = getEventStyle(ev.type||"");
                const color  = actor?getAvatarColor(actor):null;
                const initials = actor?getInitials(actor):null;
                return(
                  <li key={i} className="relative flex gap-3 pb-5 pl-1 group">
                    <div className="relative z-10 flex-shrink-0 mt-0.5">
                      <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${style.dot}
                        ring-4 ring-card/60 shadow-md shadow-primary/10
                        flex items-center justify-center text-[11px] font-bold text-white
                        group-hover:scale-110 group-hover:ring-primary/20 transition-all duration-200`}>
                        {style.icon}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 pt-0.5">
                      <p className="text-xs font-semibold text-foreground leading-snug truncate">{ev.type}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">{formatCreatedAt(ev.createdAt)}</p>
                      {actor&&(
                        <div className="mt-1.5 flex items-center gap-1.5">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0 ring-1 ${color.ring} ${color.bg} ${color.text}`}>
                            {initials}
                          </div>
                          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border border-current/20 ${color.bg} ${color.text}`}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-2.5 h-2.5 flex-shrink-0">
                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                            </svg>
                            {actor}
                          </span>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
              {timeline.length===0&&<li className="pl-2 text-sm text-muted-foreground py-4">No activity yet</li>}
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Floating Chat Panel */}
      <FloatingChatPanel
        open={chatOpen}
        onClose={()=>setChatOpen(false)}
        messages={allMessages}
        loggedUser={loggedUser}
        message={chatMessage}
        onMessageChange={setChatMessage}
        onSend={sendChatMessage}
        sending={sendingChat}
        replyTarget={chatReply}
        onReply={setChatReply}
        onCancelReply={()=>setChatReply(null)}
        reactions={reactions}
        onReact={handleReact}
        campaignName={campaign?.campaignName}
        selectedLabel={chatLabel}
        onLabelChange={setChatLabel}
      />
      <Footer />
    </div>
  );
}